import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/AppLayout";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

const tiers = [
  { name: "FREE", price: "RM 0", period: "", desc: "Try → Live → Experience AI → Upgrade", features: ["720p · 1 destination", "4 participants", "5 live hours / month", "Basic comments & scenes", "Yes Go Live watermark", "1GB recording"] },
  { name: "CREATOR", price: "RM 79", period: "/mo", desc: "Agents, hosts & small sellers", features: ["1080p · 2 destinations", "6 participants", "Remove watermark", "Custom logo & background", "20 live hrs · 10 hrs recording", "QR CTA · 100 AI credits"] },
  { name: "PRO", price: "RM 199", period: "/mo", popular: true, desc: "Most live sellers start here", features: ["1080p · 5 destinations", "10 participants · 60 live hrs", "AI Producer + AI captions", "AI summary + AI clips", "Product cards · QR sales", "Lead capture · advanced analytics"] },
  { name: "BUSINESS", price: "RM 499", period: "/mo", desc: "Companies serious about live", features: ["8 destinations · 15 participants", "200 live hrs · 5 team seats", "CRM integration + attribution", "Battle Live + Podcast Mode", "Custom RTMP · webinar", "2,000 AI credits · priority support"] },
  { name: "ENTERPRISE", price: "RM 1,499", period: "+/mo", desc: "Agency, corporate, multi-brand", features: ["Multi-workspace · white-label", "10+ destinations · SSO + API", "Custom AI script training", "Advanced security · SLA", "Approval workflow", "Dedicated account manager"] },
];

const credits = [["500", "RM 49"], ["1,500", "RM 129"], ["5,000", "RM 349"], ["10,000", "RM 599"], ["50,000", "Custom"]];

export default function Pricing() {
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();

  const subscribe = (name) => {
    if (!user) {
      const redirectUrl = window.location.origin + "/dashboard";
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    toast.success(`${name} selected — billing coming soon (demo).`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between">
        <Brand onClick={() => navigate("/")} />
        <button data-testid="back-btn" onClick={() => navigate(user ? "/dashboard" : "/")} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border hover:bg-accent text-sm font-medium"><ArrowLeft className="h-4 w-4" /> Back</button>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-xs font-mono mb-5"><Zap className="h-3.5 w-3.5 text-[#E5253A]" /> {t("tagline")}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Turn Every Live Into Revenue</h1>
          <p className="mt-3 text-muted-foreground">SaaS subscription + AI credits. Pay 10 months, get 12 on annual plans.</p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {tiers.map((tier) => (
            <div key={tier.name} data-testid={`pricing-card-${tier.name.toLowerCase()}`} className={`relative rounded-2xl border p-5 flex flex-col ${tier.popular ? "border-[#E5253A] bg-[#E5253A]/5" : "border-border bg-card"}`}>
              {tier.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#E5253A] text-white text-[10px] font-bold">{t("most_popular")}</div>}
              <div className="text-sm font-bold tracking-widest">{tier.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-mono font-extrabold">{tier.price}</span>
                <span className="text-xs text-muted-foreground font-mono">{tier.period}</span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground min-h-[32px]">{tier.desc}</div>
              <ul className="mt-4 space-y-2 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs"><Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button data-testid={`pricing-plan-${tier.name.toLowerCase()}-subscribe`} onClick={() => subscribe(tier.name)}
                className={`mt-5 h-10 rounded-lg text-sm font-bold ${tier.popular ? "bg-[#E5253A] hover:bg-[#c71f31] text-white" : "border border-border hover:bg-accent"}`}>
                {user?.plan === tier.name ? t("current_plan") : t("subscribe")}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-bold tracking-tight">AI Credits Top-Up</h3>
            <p className="text-xs text-muted-foreground mt-1">1cr reply · 2cr caption · 5cr summary · 10cr highlight · 20cr short clip</p>
            <div className="mt-4 space-y-2">
              {credits.map(([c, p]) => (
                <div key={c} data-testid={`credit-${c}`} className="flex items-center justify-between rounded-lg border border-border px-4 py-2.5">
                  <span className="font-mono text-sm">{c} credits</span>
                  <span className="font-mono text-sm font-bold text-emerald-400">{p}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-bold tracking-tight">Add-ons</h3>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              {[["Extra Team Seat", "RM 20"], ["Extra Destination", "RM 20"], ["Extra Workspace", "RM 49"], ["+50GB Storage", "RM 39"], ["AI Clips Pro", "RM 99"], ["Live Commerce", "RM 99"], ["Battle Live", "RM 99"], ["White Label", "RM 299"], ["API Access", "RM 199"], ["CRM Integration", "RM 99"]].map(([n, p]) => (
                <div key={n} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <span>{n}</span><span className="font-mono text-muted-foreground">{p}/mo</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
